import { Avatar, Box, Stack, createStyles } from '@mantine/core';
import type { AIPersonaOut } from '@repo/api-codegen';
import { AIAvatar } from '@repo/common/components/AI/Avatar';
import { Text } from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import type { AiChatStore } from '../store';
import { AiViewportWrapper } from './AiViewportWrapper';

const useStyles = createStyles(
	(theme, { animationName }: { animationName: string }) => ({
		root: {
			flexGrow: 1,
			height: '100%',
			paddingBottom: theme.spacing.sm,
		},
		emptyStateContainer: {
			flexGrow: 1,
			display: 'flex',
			alignItems: 'center',
		},
		emptyState: {
			padding: 0,
			paddingBottom: theme.other.space[16],
		},
		emptyStateAnimated: {
			[`@keyframes ${animationName}`]: {
				'0%': {
					opacity: 0,
					transform: 'translateY(4px)',
				},
				'100%': {
					opacity: 1,
					transform: 'translateY(0)',
				},
			},
			opacity: 0,
			transform: 'translateY(4px)',
			// eslint-disable-next-line no-custom-animation/no-custom-animation
			animation: `${animationName} 400ms ease forwards 250ms`,
		},
	})
);

export const AIChatEmpty = observer(
	({
		store,
		activePersonaId,
		personas,
	}: {
		store: AiChatStore;
		activePersonaId: string;
		personas: AIPersonaOut[];
	}) => {
		const { classes } = useStyles({
			animationName: `fadeUp-${activePersonaId ? activePersonaId.substring(0, 4) : 'default'}`,
		});

		const activePersona = useMemo(
			() =>
				personas.find((persona) =>
					activePersonaId ? persona.id === activePersonaId : persona.is_default
				),
			[personas, activePersonaId]
		);

		return (
			<AiViewportWrapper h="100%">
				<Stack className={classes.root}>
					<Box className={classes.emptyStateContainer}>
						<Box className={classes.emptyStateAnimated}>
							{activePersona ? (
								<Stack spacing="sm">
									{activePersona.avatar_url ? (
										<Avatar
											src={activePersona.avatar_url}
											size="md"
											alt={`${activePersona.name} avatar`}
										/>
									) : (
										<AIAvatar size="md" speed="slow" iconSpeed="none" />
									)}
									<Text size="xl" fw={600}>
										{activePersona.name ? activePersona.name : 'New persona'}
									</Text>
									{activePersona.description && (
										<Text size="md">{activePersona.description}</Text>
									)}
								</Stack>
							) : (
								<Stack spacing="sm">
									<AIAvatar size="md" speed="slow" iconSpeed="none" />
									<Stack spacing={0}>
										<Text size="xl" fw={600}>
											Hi,{' '}
											{store.currentUser.first_name ??
												store.currentUser.display_name ??
												'there'}
										</Text>
										<Text size="xl" fw={600} color="text/secondary/default">
											What can I help with?
										</Text>
									</Stack>
								</Stack>
							)}
						</Box>
					</Box>
				</Stack>
			</AiViewportWrapper>
		);
	}
);
